import {
  addCustomerDocumentsService,
  addCustomerService,
  changeCustomerDepositRulesService,
  changeCustomerEmailService,
  changeCustomerPhoneService,
  deactivateCustomerService,
  deleteCustomerDocumentsService,
  editCustomerService,
  getCustomerDetailsService,
  getCustomerDocumentsService,
  getCustomerListForSelectionService,
  getCustomersListService,
  reactivateCustomerService,
  resetCustomerPasswordService,
  startCustomerUpdateService,
} from '@/services/customers';
import {
  UpdatePasswordFields,
  UpdateEmailFields,
  UpdatePhoneFields,
} from '@/types/common';
import {
  AddCustomerDocumentFields,
  ChangeDepositRulesFormFields,
  CustomerFormFields,
  CustomerForSelectionQueryParams,
  queryKeys,
} from '@/types/customers';
import { SelectOption } from '@/types/inputs';
import { convertObjectValuesToString } from '@/utils/responseConvertors';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetCustomerList = () => {
  const [searchParams] = useSearchParams();
  const params = searchParams.toString();

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.list, params],
    queryFn: () => getCustomersListService(params),
  });

  return {
    isSuccess,
    customers: data?.consumers.records || [],
    aggregateInfo: data?.aggregateInfo,
    isCustomersPending: isPending,
    totalRecordCount: data?.consumers.totalRecordCount || 0,
  };
};

export const useGetCustomerDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.details, uuid],
    queryFn: () => getCustomerDetailsService(uuid),
  });

  return {
    customerDetails: data,
    isCustomerDetailsPending: isPending,
  };
};

export const useGetCustomerListForSelection = (
  queryParams: CustomerForSelectionQueryParams,
  enabled = true,
) => {
  const params = new URLSearchParams(
    convertObjectValuesToString(queryParams),
  ).toString();

  const { data, isPending: isCustomerListPending } = useQuery({
    queryFn: () => getCustomerListForSelectionService(params),
    queryKey: [queryKeys.list, queryKeys.selectionList, params],
    gcTime: Infinity,
    staleTime: Infinity,
    enabled,
  });

  const customerListByName: SelectOption[] = data
    ? data.map(data => {
        return {
          ...data,
          value: data.uuid,
          label: data.fullName,
        };
      })
    : [];
  const customerListByEmail: SelectOption[] = data
    ? data.map(data => {
        return {
          ...data,
          value: data.uuid,
          label: data.email,
        };
      })
    : [];
  const customerListByBoth: SelectOption[] = data
    ? data.map(data => {
        return {
          ...data,
          value: data.uuid,
          label: `${data.fullName} (${data.email})`,
        };
      })
    : [];

  return {
    customerListByName,
    customerListByEmail,
    customerListByBoth,
    isCustomerListPending,
  };
};

export const useGetCustomerDocuments = (uuid: string) => {
  const { data, isPending, refetch } = useQuery({
    queryKey: [queryKeys.list, queryKeys.documents, uuid],
    queryFn: () => getCustomerDocumentsService(uuid),
    enabled: !!uuid,
  });

  return {
    customerDocuments: data?.consumerDocuments?.records || [],
    isCustomerDocumentsPending: isPending,
    totalRecordsCount: data?.consumerDocuments?.totalRecordCount || 0,
    refetchDocuments: refetch,
  };
};

export function useAddCustomerDocument() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (document: AddCustomerDocumentFields) =>
      addCustomerDocumentsService(document),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.documents],
      });
      queryClient.refetchQueries({
        queryKey: [queryKeys.documents],
      });
    },
  });

  return {
    addCustomerDocument: mutateAsync,
    isAddCustomerDocument: isPending,
  };
}

export function useDeleteCustomerDocument() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => deleteCustomerDocumentsService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.documents],
      });
      queryClient.refetchQueries({
        queryKey: [queryKeys.documents],
      });
    },
  });

  return {
    deleteCustomerDocument: mutateAsync,
    isDeleteCustomerDocument: isPending,
  };
}

export function useAddCustomer() {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: CustomerFormFields) =>
      addCustomerService({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    addCustomer: mutateAsync,
    isAddCustomerPending: isPending,
  };
}

export function useEditCustomer() {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: CustomerFormFields) =>
      editCustomerService({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    editCustomer: mutateAsync,
    isEditCustomerPending: isPending,
  };
}

export const useDeactivateCustomer = (uuid: string, enabled = false) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => deactivateCustomerService(uuid, false),
    onSuccess: () => clearQueryCache(queryClient),
  });
  const { data, isFetching: isCanDeactivateCustomerPending } = useQuery({
    queryKey: [queryKeys.preflight, uuid],
    queryFn: () => deactivateCustomerService(uuid, true),
    enabled,
  });

  return {
    canDeactivateCustomer: data?.canDeactivate || false,
    isCanDeactivateCustomerPending,
    deactivateCustomer: mutateAsync,
    isDeactivateCustomerPending: isPending,
  };
};

export function useReactivateCustomer(uuid: string) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => reactivateCustomerService(uuid),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    reactivateCustomer: mutateAsync,
    isReactivateCustomerPending: isPending,
  };
}

export function useResetCustomerPassword(uuid: string) {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: UpdatePasswordFields) =>
      resetCustomerPasswordService({ ...data, uuid }),
  });

  return {
    resetCustomerPassword: mutateAsync,
    isResetCustomerPasswordPending: isPending,
  };
}

export function useStartCustomerUpdate(uuid: string) {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => startCustomerUpdateService(uuid),
  });

  return {
    startCustomerUpdate: mutateAsync,
    isStartCustomerUpdatePending: isPending,
  };
}

export function useChangeCustomerEmail(uuid: string) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: UpdateEmailFields) =>
      changeCustomerEmailService({ ...data, uuid }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    changeCustomerEmail: mutateAsync,
    isChangeCustomerEmailPending: isPending,
  };
}

export function useChangeCustomerPhone(uuid: string) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: UpdatePhoneFields) =>
      changeCustomerPhoneService({ ...data, uuid }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    changeCustomerPhone: mutateAsync,
    isChangeCustomerPhonePending: isPending,
  };
}

export function useChangeCustomerDepositRules() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: ChangeDepositRulesFormFields) =>
      changeCustomerDepositRulesService(data),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    changeCustomerDepositRules: mutateAsync,
    isChangeCustomerDepositRulesPending: isPending,
  };
}
